import React from 'react';
import CacheResultsContext from '../components/CacheResultsContext';
import { cacheResultForViewsFilterOptionsKey } from '../libs/cacheKeys';
import {
  fetchCustomDataFields,
  fetchEventOptions,
  fetchResourceOptions
} from '../libs/listViewRequests';
import type { CustomDataTreeNode } from '../listview/types';

interface ListViewFilterOptions {
  resourceGroups: string[] | null;
  resourceRoles: string[] | null;
  eventStatuses: string[] | null;
  eventProfiles: string[] | null;
  resourceCustomDataFields: CustomDataTreeNode[] | null;
  resourceBookingCustomDataFields: CustomDataTreeNode[] | null;
}

const emptyFilterOptions: ListViewFilterOptions = {
  resourceGroups: null,
  resourceRoles: null,
  eventStatuses: null,
  eventProfiles: null,
  resourceCustomDataFields: null,
  resourceBookingCustomDataFields: null
};

const cachedFilterOptions = (
  getCacheResultValue: (key: string) => unknown,
  cacheKey: string
): ListViewFilterOptions =>
  (getCacheResultValue(cacheKey) as ListViewFilterOptions | null) || emptyFilterOptions;

/* What the filter panel of a view offers: the installation's complete
 * resource groups/roles, event statuses/profiles and custom data field
 * schemas, from /resourceoptions, /eventoptions and /customdatafields.
 *
 * Every one of these requests fails silently: without the resource or event
 * options those filters fall back to the values occurring in the bookings,
 * and without a custom data schema that domain's filters are not offered
 * (there is no bookings-derived fallback for those). A failure is therefore
 * cached as "still missing" rather than as an answer, and the next view
 * opened retries exactly the part that failed. */
const useListViewFilterOptions = (host: string): ListViewFilterOptions => {
  const { getCacheResultValue, setCacheResultValue } =
    React.useContext(CacheResultsContext);
  const cacheKey = cacheResultForViewsFilterOptionsKey(host);

  const [filterOptions, setFilterOptions] = React.useState(() =>
    cachedFilterOptions(getCacheResultValue, cacheKey)
  );

  React.useEffect(() => {
    let merged = cachedFilterOptions(getCacheResultValue, cacheKey);
    const resourceOptionsMissing = !merged.resourceGroups || !merged.resourceRoles;
    const eventOptionsMissing = !merged.eventStatuses || !merged.eventProfiles;
    const resourceFieldsMissing = !merged.resourceCustomDataFields;
    const resourceBookingFieldsMissing = !merged.resourceBookingCustomDataFields;

    let pending = [
      resourceOptionsMissing,
      eventOptionsMissing,
      resourceFieldsMissing,
      resourceBookingFieldsMissing
    ].filter(Boolean).length;
    let cancelled = false;
    const answered = (patch: Partial<ListViewFilterOptions> | null) => {
      if (cancelled) return;
      if (patch) {
        merged = { ...merged, ...patch };
        setFilterOptions(merged);
      }
      pending -= 1;
      if (pending === 0) setCacheResultValue(cacheKey, merged, false);
    };

    if (resourceOptionsMissing)
      fetchResourceOptions(host, (options, error) =>
        answered(
          error || !options
            ? null
            : {
                resourceGroups: options.resourceGroups,
                resourceRoles: options.resourceRoles
              }
        )
      );
    if (eventOptionsMissing)
      fetchEventOptions(host, (options, error) =>
        answered(
          error || !options
            ? null
            : {
                eventStatuses: options.eventStatuses,
                eventProfiles: options.eventProfiles
              }
        )
      );
    if (resourceFieldsMissing)
      fetchCustomDataFields(host, 'resources', (fields, error) =>
        answered(
          error || !fields ? null : { resourceCustomDataFields: fields.customDataTree }
        )
      );
    if (resourceBookingFieldsMissing)
      fetchCustomDataFields(host, 'resourcebookings', (fields, error) =>
        answered(
          error || !fields
            ? null
            : { resourceBookingCustomDataFields: fields.customDataTree }
        )
      );

    return () => {
      cancelled = true;
    };
  }, [host]);

  return filterOptions;
};

export default useListViewFilterOptions;
